import React from 'react';

/**
 * Toggle component for switching between AND and OR search modes.
 * Controlled component that receives mode and onChange callback from parent.
 * 
 * @param {Object} props
 * @param {'and' | 'or'} props.mode - Current search mode ('and' or 'or')
 * @param {Function} props.onChange - Callback when mode changes, receives new mode value
 * @param {boolean} props.disabled - Whether the toggle is disabled (when < 2 ingredients selected)
 */
export default function SearchModeToggle({
  mode = 'and',
  onChange,
  disabled = false,
}) {
  const handleModeChange = (newMode) => {
    if (disabled || newMode === mode) return;
    onChange?.(newMode);
  };

  return (
    <div
      className={`inline-flex items-center rounded-full p-1 transition-all duration-200 ${
        disabled
          ? 'bg-secondary/5 cursor-not-allowed'
          : 'bg-secondary/10'
      }`}
      role="radiogroup"
      aria-label="Search mode"
      aria-disabled={disabled}
    >
      {/* AND Mode Button */}
      <button
        type="button"
        role="radio"
        aria-checked={mode === 'and'}
        onClick={() => handleModeChange('and')}
        disabled={disabled}
        className={`px-4 py-2 rounded-full text-14 font-medium transition-all duration-200 ${
          mode === 'and'
            ? disabled
              ? 'bg-white/60 text-secondary/40 shadow-sm'
              : 'bg-emerald-600 text-white shadow-md'
            : disabled
              ? 'text-secondary/30 cursor-not-allowed'
              : 'text-secondary/70 hover:text-secondary hover:bg-white/50'
        }`}
      >
        ALL
      </button>

      {/* OR Mode Button */}
      <button
        type="button"
        role="radio"
        aria-checked={mode === 'or'}
        onClick={() => handleModeChange('or')}
        disabled={disabled}
        className={`px-4 py-2 rounded-full text-14 font-medium transition-all duration-200 ${
          mode === 'or'
            ? disabled
              ? 'bg-white/60 text-secondary/40 shadow-sm'
              : 'bg-emerald-600 text-white shadow-md'
            : disabled
              ? 'text-secondary/30 cursor-not-allowed'
              : 'text-secondary/70 hover:text-secondary hover:bg-white/50'
        }`}
      >
        ANY
      </button>
    </div>
  );
}
